import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FMT = { dmy: "dd-MM-yyyy", iso: "yyyy-MM-dd" };
const toDate = (v, mode) => {
  if (!v) return undefined;
  for (const f of [FMT[mode], FMT.iso, FMT.dmy, "dd/MM/yyyy"]) {
    const d = parse(v, f, new Date());
    if (isValid(d)) return d;
  }
  return undefined;
};
const DROPDOWN_CLASSES = {
  caption_label: "hidden", caption_dropdowns: "flex gap-1", vhidden: "sr-only",
  dropdown: "rounded-md border border-slate-200 bg-white px-1.5 py-1 text-sm focus:outline-none",
};
const THIS_YEAR = new Date().getFullYear();

/** value/onChange in `mode` format (dmy = DD-MM-YYYY, iso = YYYY-MM-DD); min/max are ISO. */
export function DatePicker({ value, onChange, mode = "dmy", min, max, testid, className, placeholder = "Pick a date", fromYear = 1940, toYear = THIS_YEAR + 5, clearable }) {
  const [open, setOpen] = useState(false);
  const d = toDate(value, mode);
  const minD = toDate(min, "iso");
  const maxD = toDate(max, "iso");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testid}
            className={cn("h-9 w-full justify-start gap-2 px-3 font-normal", clearable && d && "pr-8", !d && "text-slate-400", className)}>
            <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate">{d ? format(d, "dd-MM-yyyy") : placeholder}</span>
          </Button>
        </PopoverTrigger>
        {clearable && d && (
          <button type="button" aria-label="Clear date" data-testid={testid && `${testid}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700"
            onClick={() => onChange("")}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={d} defaultMonth={d || minD} captionLayout="dropdown-buttons"
          fromYear={minD ? minD.getFullYear() : fromYear} toYear={maxD ? maxD.getFullYear() : toYear}
          disabled={[minD && { before: minD }, maxD && { after: maxD }].filter(Boolean)} classNames={DROPDOWN_CLASSES}
          onSelect={(x) => { if (x) { onChange(format(x, FMT[mode])); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

/** Pick several dates; value/onChange is a list of DD-MM-YYYY strings. */
export function MultiDatePicker({ value = [], onChange, testid }) {
  const dates = value.map((v) => toDate(v, "dmy")).filter(Boolean);
  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="h-9 w-full justify-start gap-2 px-3 font-normal" data-testid={testid}>
            <CalendarDays className="h-4 w-4 text-slate-400" /> {value.length ? `${value.length} date(s) selected` : "Pick dates"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="multiple" selected={dates} captionLayout="dropdown-buttons" fromYear={THIS_YEAR - 1} toYear={THIS_YEAR + 2}
            classNames={DROPDOWN_CLASSES}
            onSelect={(xs) => onChange((xs || []).sort((a, b) => a - b).map((x) => format(x, FMT.dmy)))} />
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {v}<X className="h-3 w-3 cursor-pointer" onClick={() => onChange(value.filter((x) => x !== v))} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5));

/** 12-hour picker; value/onChange stay 24-hour "HH:MM" for the backend. */
export function TimePicker({ value, onChange, testid }) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  const H = m ? +m[1] : null;
  const cur = { h: H === null ? "" : String(H % 12 || 12), min: m ? m[2] : "", ap: H === null ? "AM" : H >= 12 ? "PM" : "AM" };
  const emit = (patch) => {
    const n = { ...cur, ...patch };
    const h12 = +(n.h || 12);
    onChange(`${pad((h12 % 12) + (n.ap === "PM" ? 12 : 0))}:${n.min || "00"}`);
  };
  const sel = (val, key, items, w, label) => (
    <Select value={val} onValueChange={(v) => emit({ [key]: v })}>
      <SelectTrigger className={`h-9 ${w} px-2`} data-testid={testid && `${testid}-${key}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="max-h-60">{items.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {sel(cur.h, "h", HOURS, "w-[62px]", "hh")}
      <span className="text-slate-400">:</span>
      {sel(cur.min, "min", MINUTES, "w-[62px]", "mm")}
      {sel(cur.ap, "ap", ["AM", "PM"], "w-[66px]", "AM")}
    </div>
  );
}
